import { Box, Typography, IconButton, Stack, CircularProgress, Select, Option } from "@mui/joy";
import ApplicantItem from "./ApplicantItem";
import { useNavigate, useSearchParams } from "react-router-dom";
import BackBtn from "../../assets/svgs/Back-btn.svg";
import { ReactComponent as FilterIcon } from "../../assets/svgs/filterIcon.svg";
import { ReactComponent as SwapIcon } from "../../assets/svgs/swapIcon.svg";
import { useEffect, useState, useMemo } from "react";
import ApplicantFiltersModal from "components/modals/ApplicantFilters/ApplicantFiltersModal";
import { AppDispatch, RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllApplicantsData } from "../../redux/adminSlice";
import Pagination from "../../components/common/Pagination";
import { convertUTCToPacificDisplay } from "../../utils/helpers";

type StatusType = "Published" | "Unpublished";

interface ApplicantData {
  uuid: string;
  role: string;
  fullName: string;
  email: string | null;
  phoneNo: string;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  applicants: Array<{
    uuid: string;
    userId: string;
    positions: string | null;
    videoUuid: string;
    resumeDocumentId: string | null;
    applicationStatus: string;
    superApplied: boolean;
    zipCode: string;
    rating: number | null;
    lastActiveAt: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  }>;
}

const AllApplicantsView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState("DESC");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  
  // Get current page from URL params, default to 1
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  
  // Get items per page from URL params, default to 10
  const itemsPerPage = parseInt(searchParams.get('pageSize') || '10', 10);
  
  // Validate page size to ensure it's one of the allowed values
  const validPageSizes = [5, 10, 25, 50, 100];
  const ITEMS_PER_PAGE = validPageSizes.includes(itemsPerPage) ? itemsPerPage : 10;

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const applicantsData = useSelector(
    (state: RootState) => state?.admin?.applicantsData?.data
  );
  const loading = useSelector((state: RootState) => state.admin.status);
  
  // Derive filters directly from URL parameters (no separate state needed)
  const filters = useMemo(() => ({
    lastActiveStartUtc: searchParams.get('lastActiveStartUtc') || '',
    lastActiveEndUtc: searchParams.get('lastActiveEndUtc') || '',
    zipCode: searchParams.get('zipCode') || '',
    positions: searchParams.get('positions') ? searchParams.get('positions')!.split(',') : [],
    submittedToEmployerPositionIds: searchParams.get('submittedToEmployerPositionIds') ? searchParams.get('submittedToEmployerPositionIds')!.split(',') : [],
    verifiedPhone: searchParams.get('verifiedPhone') === 'true' ? true : searchParams.get('verifiedPhone') === 'false' ? false : null,
    hasVideo: searchParams.get('hasVideo') === 'true' ? true : searchParams.get('hasVideo') === 'false' ? false : null,
    hasResume: searchParams.get('hasResume') === 'true' ? true : searchParams.get('hasResume') === 'false' ? false : null,
    superApplied: searchParams.get('superApplied') === 'true' ? true : searchParams.get('superApplied') === 'false' ? false : null,
    minRating: searchParams.get('minRating') ? Number(searchParams.get('minRating')) : null,
    maxRating: searchParams.get('maxRating') ? Number(searchParams.get('maxRating')) : null,
    isActive: searchParams.get('isActive') === 'true' ? true : searchParams.get('isActive') === 'false' ? false : null,
    isPublished: searchParams.get('isPublished') === 'true' ? true : searchParams.get('isPublished') === 'false' ? false : null,
    isGloballyVisible: searchParams.get('isGloballyVisible') === 'true' ? true : searchParams.get('isGloballyVisible') === 'false' ? false : null,
    fullNameLike: searchParams.get('fullNameLike') || null,
  }), [searchParams]);
  
  const payload = useMemo(() => ({
    page: currentPage,
    limit: ITEMS_PER_PAGE,
    lastActiveStartUtc: filters.lastActiveStartUtc,
    lastActiveEndUtc: filters.lastActiveEndUtc,
    zipCode: filters.zipCode,
    positions: filters.positions,
    submittedToEmployerPositionIds: filters.submittedToEmployerPositionIds,
    verifiedPhone: filters.verifiedPhone,
    hasVideo: filters.hasVideo,
    hasResume: filters.hasResume,
    superApplied: filters.superApplied,
    minRating: filters.minRating,
    maxRating: filters.maxRating,
    isActive: filters.isActive,
    isPublished: filters.isPublished,
    isGloballyVisible: filters.isGloballyVisible,
    fullNameLike: filters.fullNameLike,
    sort: sort,
    userUuid: "",
  }), [currentPage, ITEMS_PER_PAGE, filters, sort]);

  useEffect(() => {
    console.log('Dispatching fetchAllApplicantsData with payload:', payload);
    dispatch(fetchAllApplicantsData(payload));
  }, [dispatch, payload]);

  const openFilters = () => setIsFiltersOpen(true);
  const closeFilters = () => setIsFiltersOpen(false);

  const handleApplyFilters = (newFilters: typeof filters) => {
    closeFilters();
    
    // Update URL parameters with all filter values
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', '1'); // Reset to page 1 when applying filters
    
    // Add/update filter parameters in URL
    if (newFilters.zipCode) newSearchParams.set('zipCode', newFilters.zipCode);
    else newSearchParams.delete('zipCode');
    
    if (newFilters.positions.length > 0) newSearchParams.set('positions', newFilters.positions.join(','));
    else newSearchParams.delete('positions');
    
    if (newFilters.submittedToEmployerPositionIds.length > 0) newSearchParams.set('submittedToEmployerPositionIds', newFilters.submittedToEmployerPositionIds.join(','));
    else newSearchParams.delete('submittedToEmployerPositionIds');
    
    if (newFilters.verifiedPhone !== null) newSearchParams.set('verifiedPhone', newFilters.verifiedPhone.toString());
    else newSearchParams.delete('verifiedPhone');
    
    if (newFilters.hasVideo !== null) newSearchParams.set('hasVideo', newFilters.hasVideo.toString());
    else newSearchParams.delete('hasVideo');
    
    if (newFilters.hasResume !== null) newSearchParams.set('hasResume', newFilters.hasResume.toString());
    else newSearchParams.delete('hasResume');
    
    if (newFilters.superApplied !== null) newSearchParams.set('superApplied', newFilters.superApplied.toString());
    else newSearchParams.delete('superApplied');
    
    if (newFilters.minRating !== null) newSearchParams.set('minRating', newFilters.minRating.toString());
    else newSearchParams.delete('minRating');
    
    if (newFilters.maxRating !== null) newSearchParams.set('maxRating', newFilters.maxRating.toString());
    else newSearchParams.delete('maxRating');
    
    if (newFilters.lastActiveStartUtc) newSearchParams.set('lastActiveStartUtc', newFilters.lastActiveStartUtc);
    else newSearchParams.delete('lastActiveStartUtc');
    
    if (newFilters.lastActiveEndUtc) newSearchParams.set('lastActiveEndUtc', newFilters.lastActiveEndUtc);
    else newSearchParams.delete('lastActiveEndUtc');
    
    if (newFilters.isActive !== null) newSearchParams.set('isActive', newFilters.isActive.toString());
    else newSearchParams.delete('isActive');
    
    if (newFilters.isPublished !== null) newSearchParams.set('isPublished', newFilters.isPublished.toString());
    else newSearchParams.delete('isPublished');
    
    if (newFilters.isGloballyVisible !== null) newSearchParams.set('isGloballyVisible', newFilters.isGloballyVisible.toString());
    else newSearchParams.delete('isGloballyVisible');
    
    if (newFilters.fullNameLike) newSearchParams.set('fullNameLike', newFilters.fullNameLike);
    else newSearchParams.delete('fullNameLike');
    
    setSearchParams(newSearchParams, { replace: true });
  };
  
  const handlePageSizeChange = (newPageSize: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('pageSize', newPageSize.toString());
    newSearchParams.set('page', '1'); // Reset to page 1 when changing page size
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleBackClick = () => {
    navigate("/admin/dashboard");
  };

  const getDisplayStatus = (applicationStatus: string): StatusType => {
    return applicationStatus === "publish" ? "Published" : "Unpublished";
  };

  const toggleSort = () => {
    setSort((prevSort) => (prevSort === "ASC" ? "DESC" : "ASC"));
  };
  const handleApplicantClick = (applicant: ApplicantData) => {
    // Construct the current URL with all search parameters
    const currentUrl = `/admin/applicants${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    
    navigate(`/admin/applicants/${applicant?.applicants[0]?.userId}`, {
      state: { returnUrl: currentUrl }
    });
  };

  return (
    <>
      {loading === "loading" ? (
        <div className="flex justify-center items-center h-[100vh]">
          <CircularProgress size="lg" />
        </div>
      ) : (
        <Box p="5px" className="max-w-2xl mx-auto w-full">
          <Box
            sx={{
              mb: { xs: 1, lg: 2 },
              display: "flex",
              justifyContent: "start",
              alignItems: "start",
            }}
          >
            <Box
              component="img"
              onClick={handleBackClick}
              src={BackBtn}
              alt="Back"
              sx={{
                width: { xs: 24, lg: 32 },
                height: { xs: 24, lg: 32 },
                cursor: "pointer",
              }}
            />
          </Box>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography level="modalHeading">People</Typography>
            <Stack direction="row" spacing={1}>
              <IconButton variant="plain" color="neutral">
                <FilterIcon onClick={openFilters} />
              </IconButton>
              <IconButton variant="plain" color="neutral">
                <SwapIcon onClick={toggleSort} />
              </IconButton>
            </Stack>
          </Stack>

          <Box
            borderRadius="16px"
            bgcolor="#FFFFFF"
            overflow="auto"
            sx={{ maxHeight: "calc(100vh - 300px)", scrollbarWidth: "none" }}
          >
            {applicantsData?.users?.length > 0 ? (
              <>
                {applicantsData?.users?.map(
                  (applicant: ApplicantData, index: number) => (
                    <ApplicantItem
                      key={applicant.uuid}
                      applicantUuid={applicant.uuid}
                      fullName={applicant.fullName}
                      date={convertUTCToPacificDisplay(applicant.applicants[0]?.lastActiveAt || applicant.createdAt) || ""}
                      status={getDisplayStatus(
                        applicant.applicants[0]?.applicationStatus ||
                          "unpublish"
                      )}
                      isLast={index === applicantsData.users.length - 1}
                      onApplicantClick={() => handleApplicantClick(applicant)}
                      hasVerifiedPhone={applicant.verified && Boolean(applicant.phoneNo)}
                      hasVideo={Boolean(applicant.applicants[0]?.videoUuid)}
                      hasResume={Boolean(applicant.applicants[0]?.resumeDocumentId)} // TODO: Add resume field when available
                      hasSuperApplied={Boolean(applicant.applicants[0]?.superApplied)}
                      rating={applicant.applicants[0]?.rating}
                    />
                  )
                )}
              </>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="200px"
                flexDirection="column"
                gap={1}
              >
                <Typography level="body-md" color="neutral">
                  No applicants found
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {applicantsData?.pagination?.totalPages > 0 && (
        <Box maxWidth='lg' mx='auto'>
          <Stack
            direction={{xs: "column", md: "row"}}
            width='lg'
            maxWidth='lg'
            spacing={1}
            sx={{alignItems: 'center', justifyContent: 'space-between', mt: 2, px: 2 }}>
            <Pagination
              currentPage={currentPage}
              totalItems={applicantsData?.pagination?.total || 0}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={(page) => {
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set('page', page.toString());
                setSearchParams(newSearchParams, { replace: true });
              }}
            />
            <Stack
              direction="column"
              spacing={1}
              alignItems='center'
            >
              <Typography level="body-sm" color="neutral" sx={{ fontSize: "13px", fontWeight: "400", color: "#7B7F85" }}>
                per page
              </Typography>
              <Select
                value={ITEMS_PER_PAGE}
                onChange={(_, value) => handlePageSizeChange(value as number)}
                size="sm"
                sx={{ minWidth: 70, height: 40, boxShadow: "none"}}
              >
                <Option value={5}>5</Option>
                <Option value={10}>10</Option>
                <Option value={25}>25</Option>
                <Option value={50}>50</Option>
                <Option value={100}>100</Option>
              </Select>
            </Stack>
          </Stack>
        </Box>
      )}

      <ApplicantFiltersModal
        open={isFiltersOpen}
        onClose={closeFilters}
        onApply={handleApplyFilters}
        currentFilters={filters}
      />
    </>
  );
};

export default AllApplicantsView;